// SchemaObject.js
import { formatISO, formatISODuration, intervalToDuration } from 'date-fns';

import {
  getPoster,
  getAssetManifest,
  getAssetTrailer,
  getUserPicture,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';
import { localeConvertor } from '../resource/i18nCommon.js';

import faviconPngSource from '../../img/favicon.png';

/**
 * get coporation
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getCoporation = ({
  origin = 'https://swag.live',
  langPath = '',
} = {}) => {
  const logoImageSource = new URL(
    faviconPngSource.src || faviconPngSource,
    origin
  ).href;
  return {
    '@type': 'Corporation',
    '@id': `${origin}${langPath}/#corporation`,
    name: 'SWAG',
    url: `${origin}${langPath}`,
    logo: {
      '@type': 'ImageObject',
      '@id': `${origin}${langPath}/#logo`,
      url: logoImageSource,
      contentUrl: logoImageSource,
      width: 512,
      height: 512,
      caption: 'SWAG',
    },
    image: {
      '@id': `${origin}${langPath}/#logo`,
    },
    sameAs: [
      'https://zh.wikipedia.org/zh-tw/Swag',
      'https://twitter.com/swaglive_zh',
      'https://www.instagram.com/swaglive.zh',
      'https://www.youtube.com/@SWAGLIVE',
      'https://www.youtube.com/@swaglive4483',
      'https://t.me/swag',
      'https://www.facebook.com/sqag.live',
      'https://www.tiktok.com/@swaglive_jp',
      'https://discord.com/invite/BgNhKVwE5d',
    ],
  };
};

/**
 * get interaction counter object
 * @param {string} {type} - type (ex: 'BuyAction', 'LikeAction')
 * @param {number} {count} - action count
 * @return {Object}
 */
export const getInteractionCounter = ({ type, count }) => {
  return {
    '@type': 'InteractionCounter',
    interactionType: type.startsWith('https')
      ? type
      : `https://schema.org/${type}`,
    userInteractionCount: Math.floor(count),
  };
};

/**
 * get person object
 * @param {string} {id} - user id
 * @param {string} {username} - username
 * @param {string} {biography} - biography
 * @param {array} {hasOfferCatalog} - item list of video objects
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getPerson = ({
  id,
  username,
  biography,
  hasOfferCatalog,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  return {
    '@type': 'Person',
    '@id': `${origin}${langPath}/user/${id}#person`,
    url: `${origin}${langPath}/user/${id}`,
    name: username,
    description: biography,
    sameAs: username ? [`${origin}${langPath}/u/${username}`] : undefined,
    image: {
      '@type': 'ImageObject',
      '@id': `${origin}${langPath}/user/${id}#avatar`,
      url: getUserPicture({
        userId: id,
        size: 128,
        format: MediaAssetFormat.JPG,
      }).href,
      width: 128,
      height: 128,
    },
    hasOfferCatalog,
  };
};

/**
 * get flix video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {title} - title
 * @param {string} {username} - author name
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getFlixVideoObject = ({
  id,
  username,
  title,
  description,
  durationSecs,
  author,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic,
  encodings,
  associatedMedia,
  origin = 'https://swag.live',
  langPath = '',
  hashtags = [],
  categories = [],
  sourceLanguage,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const serialCode = title?.replace(/^\[([^[\]]*)\].*/, '$1');
  const lang = sourceLanguage
    ? localeConvertor({ locale: sourceLanguage })
    : undefined;
  let offers = undefined;
  if (!isFree) {
    offers = {
      '@type': 'Offer',
      price: unlockPrice,
      priceCurrency: 'XXX' /* 表示不是法幣 */,
      availability: 'https://schema.org/InStock',
      category: 'Pay-per-view',
      description: `Unlock ${durationSecs} seconds flix with ${unlockPrice} Diamonds (platform credits).`,
    };
  }

  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/post/${id}#full`,
    url: `${origin}${langPath}/post/${id}`,
    name: `${title || username}${isFree ? '' : ' - 完整版需付費解鎖'}`,
    description: `${description}${isFree ? '' : ' - 完整版需付費解鎖'}`,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    keywords: [serialCode, 'SWAG'].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
    inLanguage: lang,
    embedUrl: `${origin}${langPath}/post/${id}`,
    publisher: getCoporation({ origin, langPath }),
    offers,
    isFamilyFriendly: false,
    requiresSubscription: !isFree,
    ...(hashtags.length > 0 || categories.length > 0
      ? {
          about: [
            ...hashtags.map(hashtag => ({
              '@type': 'Thing',
              name: hashtag,
              url: `${origin}${langPath}/hashtag/${hashtag}/flix`,
            })),
            ...categories.map(category => ({
              '@type': 'Thing',
              name: category,
              url: `${origin}${langPath}/category/${category}/flix`,
            })),
          ],
        }
      : {}),
  };
};

/**
 * get flix preview video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {username} - author name
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getFlixPreviewVideoObject = ({
  id,
  assetId,
  username,
  title,
  description,
  durationSecs = 10,
  author,
  postedAtUnix,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/post/${id}#preview`,
    url: `${origin}${langPath}/post/${id}`,
    name: `${title || username} - 30 秒免費預告片段`,
    description: `${description} - 30 秒免費預告片段`,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetTrailer({
      id,
      assetId,
      format: MediaAssetFormat.MP4,
      preset: MediaPreset.SD,
      duration: durationSecs,
    }),
    encodingFormat: 'video/mp4',
    author,
    uploadDate,
    isAccessibleForFree: true,
    embedUrl: `${origin}${langPath}/post/${id}`,
    isFamilyFriendly: false,
    requiresSubscription: false,
    isPartOf: {
      '@id': `${origin}${langPath}/post/${id}#full`,
    },
  };
};

/**
 * get flix hls video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {title} - title
 * @param {string} {description} - description
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {bool} {isFree}
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getFlixHlsVideoObject = ({
  id,
  assetId,
  title,
  description,
  durationSecs,
  author,
  postedAtUnix,
  isFree,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/flix/${id}#video-hls`,
    url: `${origin}${langPath}/flix/${id}`,
    name: title,
    description,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    encodesCreativeWork: {
      '@id': `${origin}${langPath}/flix/${id}#video-dash`,
    },
  };
};

/**
 * get story video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {username} - to compose story title
 * @param {string} {caption} - caption
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} [{encodings}]
 * @param {object} [{associatedMedia}]
 * @param {bool} {isFree}
 * @param {array} {categories} - message categories
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getStoryVideoObject = ({
  id,
  assetId,
  username,
  title,
  titleTail,
  caption,
  durationSecs = 10,
  author,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic,
  encodings,
  associatedMedia,
  categories = [],
  origin = 'https://swag.live',
  langPath = '',
  sourceLanguage,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const name =
    `${username || '(unknown)'} - ${title || '(empty)'} ${titleTail || ''}`.trim();
  const lang = sourceLanguage
    ? localeConvertor({ locale: sourceLanguage })
    : undefined;
  let offers = undefined;
  if (!isFree) {
    offers = {
      '@type': 'Offer',
      price: unlockPrice,
      priceCurrency: 'XXX' /* 表示不是法幣 */,
      availability: 'https://schema.org/InStock',
      category: 'Pay-per-view',
      description: `Unlock with ${unlockPrice} Diamonds (platform credits).`,
    };
  }
  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/story/${id}#video-hls`,
    url: `${origin}${langPath}/story/${id}`,
    name,
    description: caption || name,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: isFree ? MediaPreset.SD : MediaPreset.SD_PREVIEW,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    keywords: [username, ...categories, 'SWAG'],
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    encodings,
    associatedMedia,
    inLanguage: lang,
    embedUrl: `${origin}${langPath}/story/${id}`,
    publisher: getCoporation({ origin, langPath }),
    offers,
  };
};

/**
 * get story preview video object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {string} {username} - to compose story title
 * @param {string} {caption} - caption
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getStoryPreviewVideoObject = ({
  id,
  assetId,
  username,
  title,
  titleTail,
  caption,
  durationSecs = 10,
  author,
  postedAtUnix,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;

  const name =
    `${username || '(unknown)'} - ${title || '(empty)'} ${titleTail || ''}`.trim();

  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/story/${id}#preview`,
    url: `${origin}${langPath}/story/${id}`,
    name,
    description: caption || name,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    contentUrl: getAssetManifest({
      id,
      assetId,
      format: MediaAssetFormat.M3U8,
      preset: MediaPreset.SD_PREVIEW,
    }),
    encodingFormat: 'application/vnd.apple.mpegurl',
    author,
    uploadDate,
    isAccessibleForFree: true,
    associatedMedia: {
      '@id': `${origin}${langPath}/story/${id}#video-hls`,
    },
  };
};

/**
 * get post video object
 * @param {string} {id} - message id
 * @param {string} {title} - title
 * @param {array} {inLanguage} - in languages
 * @param {object} {name} - ld+json name object.
 * @param {array} {alternateName} - alternate names
 * @param {object} {description} - ld+json description object.
 * @param {array} {alternateDescription} - alternate descriptions
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @param {array} {categories} - message categories
 * @param {string} {origin} - URL origin
 * @param {string} {offerDescription} - offer description
 * @return {Object}
 */
export const getPostVideoObject = ({
  id,
  assetId,
  title,
  inLanguage,
  name,
  alternateName,
  description,
  alternateDescription,
  durationSecs,
  author,
  username,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic,
  thumbnailUrl,
  hashtags = [],
  categories = [],
  origin = 'https://swag.live',
  offerDescription,
  langPath = '',
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  const serialCode = title?.replace(/^\[([^[\]]*)\].*/, '$1');
  let offers = undefined;
  if (!isFree) {
    offers = {
      '@type': 'Offer',
      price: unlockPrice,
      priceCurrency: 'XXX' /* 表示不是法幣 */,
      availability: 'https://schema.org/InStock',
      category: 'Pay-per-view',
      description: offerDescription,
    };
  }

  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/post/${id}#full`,
    url: `${origin}${langPath}/post/${id}`,
    inLanguage,
    name,
    alternateName,
    description,
    alternateDescription,
    duration,
    thumbnailUrl,
    contentUrl: assetId
      ? getAssetTrailer({
          id,
          assetId,
          format: MediaAssetFormat.MP4,
          preset: MediaPreset.SD,
          duration: 10,
        })
      : undefined,
    encodingFormat: assetId ? 'video/mp4' : undefined,
    keywords: [
      serialCode,
      username,
      ...hashtags.map(hashtag => `#${hashtag}`),
      ...categories,
      'SWAG',
    ].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    embedUrl: `${origin}${langPath}/post/${id}`,
    publisher: getCoporation({ origin, langPath }),
    offers,
    isFamilyFriendly: false,
    requiresSubscription: !isFree,
    ...(hashtags.length > 0 || categories.length > 0
      ? {
          about: [
            ...hashtags.map(hashtag => ({
              '@type': 'Thing',
              name: hashtag,
              url: `${origin}${langPath}/hashtag/${hashtag}/flix`,
            })),
            ...categories.map(category => ({
              '@type': 'Thing',
              name: category,
              url: `${origin}${langPath}/category/${category}/flix`,
            })),
          ],
        }
      : {}),
  };
};

/**
 * get post preview video object
 * @param {string} {id} - message id
 * @param {array} {inLanguage} - in languages
 * @param {object} {name} - ld+json name object
 * @param {array} {alternateName} - alternate names
 * @param {object} {description} - description
 * @param {array} {alternateDescription} - alternate descriptions
 * @param {number} {durationSecs = 10} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {string} {origin} - URL origin
 * @return {Object}
 */
export const getPostPreviewVideoObject = ({
  id,
  assetId,
  inLanguage,
  name,
  alternateName,
  description,
  alternateDescription,
  durationSecs = 10,
  author,
  postedAtUnix,
  thumbnailUrl,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/post/${id}#preview`,
    url: `${origin}${langPath}/post/${id}`,
    inLanguage,
    name,
    alternateName,
    description,
    alternateDescription,
    duration,
    thumbnailUrl,
    contentUrl: getAssetTrailer({
      id,
      assetId,
      format: MediaAssetFormat.MP4,
      preset: MediaPreset.SD,
      duration: durationSecs,
    }),
    encodingFormat: 'video/mp4',
    author,
    uploadDate,
    isAccessibleForFree: true,
    embedUrl: `${origin}${langPath}/post/${id}`,
    isFamilyFriendly: false,
    requiresSubscription: false,
    isPartOf: {
      '@id': `${origin}${langPath}/post/${id}#full`,
    },
  };
};

/**
 * get short schema object
 * @param {string} {id} - message id
 * @param {array} {inLanguage} - in languages
 * @param {object} {name} - shorts title
 * @param {array} {alternateName} - alternate names
 * @param {object} {description} - description
 * @param {array} {alternateDescription} - alternate descriptions
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @param {array} {categories} - message categories
 * @param {string} {origin} - URL origin
 * @param {string} {offerDescription} - offer description
 * @return {Object}
 */
export const getShortSchemaObject = ({
  id,
  inLanguage,
  name,
  alternateName,
  description,
  alternateDescription,
  durationSecs,
  author,
  username,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic,
  thumbnailUrl,
  hashtags = [],
  categories = [],
  origin = 'https://swag.live',
  langPath = '',
  offerDescription,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  let offers = undefined;
  if (!isFree) {
    offers = {
      '@type': 'Offer',
      price: unlockPrice,
      priceCurrency: 'XXX' /* 表示不是法幣 */,
      availability: 'https://schema.org/InStock',
      category: 'Pay-per-view',
      description: offerDescription,
    };
  }

  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/shorts/${id}#full`,
    url: `${origin}${langPath}/shorts/${id}`,
    name,
    alternateName,
    description,
    alternateDescription,
    duration,
    thumbnailUrl,
    keywords: [
      username,
      ...hashtags.map(hashtag => `#${hashtag}`),
      ...categories,
      'SWAG',
    ].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    inLanguage,
    embedUrl: `${origin}${langPath}/shorts/${id}`,
    publisher: getCoporation({ origin, langPath }),
    offers,
    isFamilyFriendly: false,
    requiresSubscription: !isFree,
    ...(categories.length > 0
      ? {
          about: [
            ...categories.map(category => ({
              '@type': 'Thing',
              name: category,
              url: `${origin}${langPath}/category/${category}/short`,
            })),
          ],
        }
      : {}),
  };
};

/**
 * get short preview schema object
 * @param {string} id - message id
 * @param {object} name - shorts title
 * @param {array} alternateName - alternate names
 * @param {object} description - description
 * @param {array} alternateDescription - alternate descriptions
 * @param {number} durationSecs - seconds
 * @param {number} postedAtUnix - uploadDate
 * @param {string} origin - URL origin
 * @param {array} inLanguage - alternate languages
 * @return {Object}
 */
export const getShortPreviewSchemaObject = ({
  id,
  name,
  alternateName,
  description,
  alternateDescription,
  durationSecs = 10,
  author,
  postedAtUnix,
  origin = 'https://swag.live',
  langPath = '',
  inLanguage,
}) => {
  const duration = durationSecs
    ? formatISODuration(
        intervalToDuration({ start: 0, end: durationSecs * 1000 })
      )
    : undefined;
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;

  return {
    '@type': 'VideoObject',
    '@id': `${origin}${langPath}/shorts/${id}#preview`,
    url: `${origin}${langPath}/shorts/${id}`,
    name,
    alternateName,
    description,
    alternateDescription,
    duration,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: MediaPreset.SD,
    })?.href,
    author,
    uploadDate,
    isAccessibleForFree: true,
    inLanguage,
    embedUrl: `${origin}${langPath}/shorts/${id}`,
    publisher: getCoporation({ origin, langPath }),
    isFamilyFriendly: false,
    requiresSubscription: false,
    isPartOf: {
      '@id': `${origin}${langPath}/shorts/${id}#full`,
    },
  };
};

/**
 * get post image object
 * @param {string} {id} - message id
 * @param {string} {assetId} - asset id
 * @param {array} {inLanguage} - in languages
 * @param {object} {name} - ld+json name object
 * @param {array} {alternateName} - alternate names
 * @param {object} {description} - ld+json description object
 * @param {array} {alternateDescription} - alternate descriptions
 * @param {number} {durationSecs} - seconds
 * @param {object} {author} - author (Person schema object)
 * @param {number} {postedAtUnix} - uploadDate
 * @param {array} {interactionStatistic}
 * @param {array} {encodings}
 * @param {object} {associatedMedia}
 * @param {bool} {isFree}
 * @param {string} {origin} - URL origin
 * @param {string} {offerDescription} - offer description
 * @return {Object}
 */
export const getPostImageObject = ({
  id,
  inLanguage,
  name,
  alternateName,
  description,
  alternateDescription,
  author,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic,
  origin = 'https://swag.live',
  langPath = '',
  offerDescription,
}) => {
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;
  let offers = undefined;
  if (!isFree) {
    offers = {
      '@type': 'Offer',
      price: unlockPrice,
      priceCurrency: 'XXX' /* 表示不是法幣 */,
      availability: 'https://schema.org/InStock',
      category: 'Pay-per-view',
      description: offerDescription,
    };
  }

  return {
    '@type': 'ImageObject',
    '@id': `${origin}${langPath}/post/${id}#full`,
    url: `${origin}${langPath}/post/${id}`,
    inLanguage,
    name,
    alternateName,
    description,
    alternateDescription,
    thumbnailUrl: getPoster({
      id,
      size: '2048x2048',
      format: MediaAssetFormat.JPG,
      preset: isFree ? MediaPreset.SD_PREVIEW : MediaPreset.SD,
    }),
    keywords: ['SWAG'].filter(a => a),
    author,
    uploadDate,
    isAccessibleForFree: isFree,
    interactionStatistic,
    embedUrl: `${origin}${langPath}/post/${id}`,
    publisher: getCoporation({ origin, langPath }),
    offers,
    isFamilyFriendly: false,
    requiresSubscription: !isFree,
  };
};

/**
 * Get post preview image schema object.
 * @param {Object} params
 * @param {string} params.id - post id
 * @param {array} params.inLanguage - in languages
 * @param {object} params.name - ld+json name object
 * @param {array} params.alternateName - alternate names
 * @param {string} params.description - post description
 * @param {array} params.alternateDescription - alternate descriptions
 * @param {string} params.author - post author
 * @param {number} params.postedAtUnix - post unix timestamp
 * @param {boolean} params.isFree - is post free
 * @param {number} params.unlockPrice - unlock price (if not free)
 * @param {Object} params.interactionStatistic - interaction statistic
 * @param {string} [params.origin] - URL origin
 * @returns {Object} Schema.org ImageObject for post preview
 */
export const getPostPreviewImageObject = ({
  id,
  inLanguage,
  name,
  alternateName,
  description,
  alternateDescription,
  author,
  postedAtUnix,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const uploadDate = postedAtUnix ? formatISO(postedAtUnix * 1000) : undefined;

  const imageUrl = getPoster({
    id,
    size: '2048x2048',
    format: MediaAssetFormat.JPG,
    preset: MediaPreset.SD_PREVIEW,
  })?.href;

  return {
    '@type': 'ImageObject',
    '@id': `${origin}${langPath}/post/${id}#preview`,
    url: `${origin}${langPath}/post/${id}`,
    inLanguage,
    name,
    alternateName,
    description,
    alternateDescription,
    thumbnailUrl: imageUrl,
    contentUrl: imageUrl,
    encodingFormat: 'image/jpeg',
    keywords: ['SWAG'].filter(a => a),
    author,
    uploadDate,
    publisher: getCoporation({ origin, langPath }),
    isAccessibleForFree: true,
    embedUrl: `${origin}${langPath}/post/${id}`,
    isFamilyFriendly: false,
    requiresSubscription: false,
    isPartOf: {
      '@id': `${origin}${langPath}/post/${id}#full`,
    },
  };
};

/**
 * Get product schema object.
 * @param {string} {id} - product id.
 * @param {string} {type} - product type.
 * @param {object|string} {name} - product name (can be object with @value and @language for multilingual support).
 * @param {string} {title} - product name (for backward compatibility).
 * @param {string} {titleTail} - title tail wording.
 * @param {object|string} {description} - product description (can be object with @value and @language for multilingual support).
 * @param {string} {nbf} - product nbf.
 * @param {array} {interactionStatistic} - interaction statistic.
 * @param {array} {offers} - offers.
 * @param {array} {inLanguage} - supported languages.
 * @param {array} {alternateName} - alternate names in different languages.
 * @param {array} {alternateDescription} - alternate descriptions in different languages.
 * @param {string} {origin} - URL origin
 * @return {Object} Return SchemaObject
 */
export const getProductObject = ({
  inLanguage,
  id,
  type,
  name,
  title,
  titleTail,
  description,
  nbf,
  interactionStatistic,
  offers,
  origin = 'https://swag.live',
  langPath = '',
}) => {
  const logoImageSource = `${origin}/${faviconPngSource.src || faviconPngSource}`;
  const keyword = titleTail ? `${title} ${titleTail}` : title;
  return {
    '@type': 'Product',
    '@id': `${origin}${langPath}/product/${id}`,
    brand: 'SWAG',
    category: type,
    keywords: [keyword, type, 'SWAG'].filter(i => i),
    logo: {
      '@type': 'ImageObject',
      '@id': `${origin}${langPath}/#logo`,
      url: logoImageSource,
      contentUrl: logoImageSource,
      width: 512,
      height: 512,
      caption: 'SWAG',
    },
    releaseDate: nbf,
    inLanguage,
    name,
    description,
    interactionStatistic,
    offers,
  };
};
